import React from 'react';
import { Drawer } from 'components';
import MatchStatusBadge from './MatchStatusBadge';
import {
	findNextReviewOrder,
	formatLocation,
	getCandidateOption,
	getDisplayConfidence,
	getEffectiveOrderReviewState,
	getIssueMessage,
	getApplicableZoneFields,
	getLocationPresentation,
	getLocationMatch,
	getOrderCandidates,
	getOrderIdentifier,
	getImportRowIdentity,
	getReasonMessage,
	getSourceValues,
	getSourceProvenance,
	getImportDiagnostics,
	getFieldReviewEditors,
	getFieldReviewInputValue,
	normalizeFieldReviewValue,
	validateFieldReviewValue,
	getServiceCatalog,
	getServiceCohortTargets,
	getServiceArchetypeCatalog,
	getServiceCreationOption,
	getServiceCatalogDisplayList,
	getServiceSimilarityDisplayList,
	getServicePresentation,
	getZoneCoverageActions,
	getZoneCoverageChangeItems,
	getZoneCoverageItems,
	getZoneCreationOption,
	getAssignmentPresentation,
	getAssignmentMutabilityMessage,
	hasLocationSourceValue,
	isImportableMainEntityAssignmentFallback,
	isCanonicalLocationOption,
	isExistingImportOrder,
	isOrderTargetedByZoneCreation,
	LOCATION_PRESENTATION_STATE,
	ZONE_COVERAGE_ACTION,
} from './importReview.model';
import {
	getContractFrequencyPresentation,
	getContractFrequencyReviewMessage,
} from 'components/DataFieldManager/ContractFrequencyId/contractFrequencyReview.model';
import {
	CONTRACT_FREQUENCY_INTERVAL_UNITS,
	getContractFrequencyCreationCohortTargets,
	getContractFrequencyCreationDraft,
	getContractFrequencyCreationDraftErrors,
	getContractFrequencyCreationPlan,
	getContractFrequencyCreationPresentation,
} from './contractFrequencyCreation.model';
import styles from './ImportReview.module.css';

const confidenceLabel = confidence => {
	const value = Number(confidence);
	if (!Number.isFinite(value)) return;
	return `${Math.round(value <= 1 ? value * 100 : value)}% de similitud`;
};

const serviceCreationReasonLabel = reason =>
	({
		name_blank: 'Escribe un nombre para el nuevo servicio.',
		service_name_length: 'El nombre del servicio es demasiado largo.',
		payment_name_length:
			'El nombre es demasiado largo para las tareas de pago del arquetipo.',
		archetype_scope: 'El servicio base ya no está disponible en este alcance.',
		archetype_unavailable:
			'No fue posible leer la configuración del servicio base.',
		normalized_name_conflict:
			'Ya existe un servicio con el mismo nombre normalizado.',
		cohort_request_conflict:
			'Las filas de este grupo contienen solicitudes de creación diferentes.',
	})[reason] || 'No fue posible validar el plan de creación del servicio.';

const contractFrequencyCreationReasonLabel = reason =>
	({
		contract_required:
			'El contrato es obligatorio para preparar esta frecuencia.',
		code: 'El código no cumple el formato requerido o ya no es válido.',
		name_blank: 'Escribe un nombre para la frecuencia.',
		name_length: 'El nombre de la frecuencia excede 80 caracteres.',
		interval_count: 'El intervalo debe ser un entero entre 1 y 2147483647.',
		interval_unit: 'Selecciona una unidad de frecuencia válida.',
		aliases: 'Los aliases no cumplen los límites permitidos.',
		source_alias_required:
			'El valor del archivo debe conservarse como alias de la frecuencia.',
		namespace_ambiguous:
			'El nombre, código o alias coincide con varias frecuencias existentes.',
		namespace_conflict:
			'La frecuencia propuesta tiene un conflicto con el catálogo existente.',
	})[reason] ||
	'No fue posible validar el plan de frecuencia. Revisa los datos y vuelve a prepararlo.';

const getZoneLabel = field => {
	if (field?.id === 'contractZoneId') return 'Zona de contrato';
	if (field?.id === 'orderZoneId') return 'Zona de orden';
	return field?.name || 'Ubicación';
};

const DEFAULT_ZONE_LEVEL_LABELS = {
	city: 'Ciudad',
	township: 'Cantón',
	zone: 'Zona',
};

const FEMININE_ZONE_LEVEL_LABELS = new Set([
	'ciudad',
	'comuna',
	'provincia',
	'región',
	'zona',
]);

const newZoneLevelLabel = noun =>
	`${noun} ${
		FEMININE_ZONE_LEVEL_LABELS.has(noun.trim().toLocaleLowerCase('es'))
			? 'nueva'
			: 'nuevo'
	}`;

const newZoneCreationLevels = levels =>
	(Array.isArray(levels) ? levels : []).filter(
		level =>
			level?.action === 'create' && DEFAULT_ZONE_LEVEL_LABELS[level.type],
	);

const ZoneCreationLevels = ({ levels, labels = {}, result = false }) => {
	const safeLevels = (Array.isArray(levels) ? levels : []).filter(
		level => level && DEFAULT_ZONE_LEVEL_LABELS[level.type],
	);
	const visibleLevels = result ? safeLevels : newZoneCreationLevels(safeLevels);
	if (!visibleLevels.length) return null;
	const statusLabel = level => {
		const noun = String(
			labels[level.type] || DEFAULT_ZONE_LEVEL_LABELS[level.type],
		);
		if (!result) return newZoneLevelLabel(noun);
		if (level.status === 'created') return `${noun}: creación confirmada`;
		if (level.status === 'reused') return `${noun}: reutilización confirmada`;
		return `${noun}: estado no confirmado`;
	};
	return (
		<ul
			className={styles.zoneCreationLevels}
			aria-label={
				result
					? 'Resultado del cambio de jerarquía'
					: 'Jerarquía que se agregará'
			}
		>
			{visibleLevels.map(level => (
				<li key={level.type}>
					<span className={styles.zoneCreationBadge}>{statusLabel(level)}</span>
					<strong>
						{String(level.name || '').trim() || 'Nombre no confirmado'}
					</strong>
				</li>
			))}
		</ul>
	);
};

const FieldReviewEditor = ({
	order,
	editor,
	selection,
	onApply,
	onUndo,
	isSubmitting,
}) => {
	const currentValue = order?.[editor.fieldId];
	const rowIdentity = getImportRowIdentity(order);
	const initialValue = getFieldReviewInputValue(
		selection?.value ?? currentValue ?? editor.value ?? '',
		editor,
	);
	const [draft, setDraft] = React.useState(String(initialValue));
	React.useEffect(() => {
		setDraft(String(initialValue));
	}, [initialValue, editor.fieldId, rowIdentity]);

	const validation = validateFieldReviewValue(draft, editor);
	const normalizedValue = normalizeFieldReviewValue(draft, editor);
	const requiredError = validation.reason === 'required';
	const lengthError = normalizedValue.length > editor.maxLength;
	const errorMessage = requiredError
		? 'Este campo es obligatorio.'
		: validation.reason === 'format'
			? `Ingresa una fecha válida con formato ${editor.inputFormat}.`
			: validation.reason === 'min'
				? `La fecha no puede ser anterior a ${editor.min}.`
				: validation.reason === 'max'
					? `La fecha no puede ser posterior a ${editor.max}.`
					: lengthError
						? `El valor no puede superar ${editor.maxLength} caracteres.`
						: undefined;
	const diagnostics = getImportDiagnostics(order);
	const sourceValue = Object.prototype.hasOwnProperty.call(
		diagnostics.sourceFieldValues || {},
		editor.fieldId,
	)
		? diagnostics.sourceFieldValues[editor.fieldId]
		: editor.value;
	const inputId = `field-review-${rowIdentity || 'row'}-${editor.fieldId}`;
	const errorId = `${inputId}-error`;

	return (
		<section
			className={styles.drawerSection}
			aria-labelledby={`${inputId}-heading`}
		>
			<h3 id={`${inputId}-heading`}>{editor.label}</h3>
			<div className={styles.comparisonCards}>
				<div className={styles.comparisonCard}>
					<span className={styles.locationLabel}>Valor en archivo</span>
					<strong>{String(sourceValue ?? '').trim() || 'No informado'}</strong>
				</div>
				<div className={styles.comparisonCard}>
					<span className={styles.locationLabel}>Valor actual</span>
					<strong>{String(currentValue ?? '').trim() || 'Vacío'}</strong>
				</div>
			</div>
			<label className={styles.fieldReviewField} htmlFor={inputId}>
				<span>
					{editor.label}
					{editor.required === true ? ' *' : ''}
				</span>
				<input
					id={inputId}
					name={editor.fieldId}
					type={editor.kind === 'date' ? 'date' : 'text'}
					value={draft}
					required={editor.required === true}
					maxLength={editor.kind === 'scalar' ? editor.maxLength : undefined}
					min={editor.kind === 'date' ? editor.min : undefined}
					max={editor.kind === 'date' ? editor.max : undefined}
					disabled={isSubmitting}
					aria-invalid={Boolean(errorMessage)}
					aria-describedby={errorMessage ? errorId : undefined}
					onChange={event => setDraft(event.target.value)}
				/>
			</label>
			{editor.kind === 'date' && (
				<p className={styles.muted}>
					Formato {editor.inputFormat} · Zona horaria {editor.timezone}
				</p>
			)}
			{errorMessage && (
				<p id={errorId} className={styles.fieldError} role='alert'>
					{errorMessage}
				</p>
			)}
			{selection && (
				<p className={styles.muted} role='status'>
					Selección pendiente de validar.
				</p>
			)}
			<div className={styles.fieldReviewActions}>
				{selection && (
					<button
						type='button'
						className={styles.secondaryButton}
						disabled={isSubmitting}
						onClick={() => onUndo?.({ rowData: order, editor })}
					>
						Deshacer
					</button>
				)}
				<button
					type='button'
					className={styles.primaryButton}
					disabled={isSubmitting || Boolean(errorMessage)}
					onClick={() =>
						onApply?.({ rowData: order, editor, value: normalizedValue })
					}
				>
					Aplicar
				</button>
			</div>
		</section>
	);
};

const FieldReviewEditors = ({
	order,
	selections,
	onApply,
	onUndo,
	isSubmitting,
}) =>
	getFieldReviewEditors(order).map(editor => {
		const rowId = getImportRowIdentity(order);
		const selection = selections.find(
			candidate =>
				String(candidate.rowId) === String(rowId) &&
				String(candidate.fieldId) === editor.fieldId,
		);
		return (
			<FieldReviewEditor
				key={`${rowId}-${editor.fieldId}`}
				order={order}
				editor={editor}
				selection={selection}
				onApply={onApply}
				onUndo={onUndo}
				isSubmitting={isSubmitting}
			/>
		);
	});

const getIssueLabel = (issue, order) => {
	if (issue?.blocking) return 'Bloqueante';
	if (
		issue?.blocking === false &&
		(/(^|_)ASSIGNMENT_/.test(String(issue?.code || '')) ||
			issue?.field === 'assignedTechId') &&
		isImportableMainEntityAssignmentFallback(order)
	)
		return 'Auto-corregida';
	if (issue?.code === 'ZONE_COVERAGE_PROVISION_AVAILABLE')
		return 'Cambios de cobertura';
	if (issue?.code === 'ZONE_COVERAGE_TECH_NOT_PROVISIONABLE')
		return 'Cobertura sin cambios';
	if (issue?.code === 'ZONE_COVERAGE_AUZ_REQUIRES_BACKOFFICE')
		return 'AUZ requiere gestión';
	if (
		[
			'LOCATION_MATCH_NORMALIZED',
			'LOCATION_MATCH_FUZZY',
			'LOCATION_MATCH_FUZZY_LOW_SIMILARITY',
			'LOCATION_MATCH_FUZZY_AMBIGUOUS',
			'LOCATION_MISSING_ZONE_FALLBACK',
			'LOCATION_MISSING_CITY_INFERRED',
		].includes(issue?.code)
	)
		return 'Auto-corregida';
	if (
		[
			'SERVICE_MATCH_NORMALIZED',
			'SERVICE_MATCH_FUZZY',
			'SERVICE_AUTO_MATCHED',
		].includes(issue?.code)
	)
		return 'Auto-corregida';
	if (String(issue?.code || '').startsWith('SERVICE_CREATION_'))
		return 'Creación de servicio';
	return 'Revisión';
};

const getCoverageScopeLabel = item => {
	const department =
		item.departmentName ||
		item.projectName ||
		(item.projectId !== undefined
			? `departamento ${item.projectId}`
			: 'el departamento seleccionado');
	const service =
		item.serviceName ||
		(item.serviceId !== undefined
			? `servicio ${item.serviceId}`
			: 'el servicio de la orden');
	return `${department} · ${service}`;
};

const getCoverageActionLabel = (action, item) => {
	switch (action) {
		case ZONE_COVERAGE_ACTION.CREATE_AUZ:
			return 'Se creará la asignación AUZ con tipo normal.';
		case ZONE_COVERAGE_ACTION.REACTIVATE_AUZ:
			return 'Se reactivará la asignación AUZ existente.';
		case ZONE_COVERAGE_ACTION.NORMALIZE_AUZ_TYPE:
			return `Se normalizará la asignación AUZ de tipo ${
				item.auzTypeBefore || 'actual'
			} a ${item.auzTypeAfter || 'normal'}.`;
		case ZONE_COVERAGE_ACTION.CREATE_AZD:
			return 'Se creará la cobertura AZD para GPON y el servicio indicado.';
		case ZONE_COVERAGE_ACTION.REACTIVATE_AZD:
			return 'Se reactivará la cobertura AZD para GPON y el servicio indicado.';
		default:
			return;
	}
};

const CoverageReview = ({ order, assignedTechIdField }) => {
	const provisioningItems = getZoneCoverageChangeItems([order]);
	const warningItems = getZoneCoverageItems(order).filter(
		item => item.status === 'warning_only',
	);
	const items = [...provisioningItems, ...warningItems];
	if (!items.length) return null;

	return (
		<section
			className={styles.drawerSection}
			aria-labelledby='coverage-heading'
		>
			<h3 id='coverage-heading'>Cobertura operativa</h3>
			<ul className={styles.coverageList}>
				{items.map((item, index) => {
					const warningOnly = item.status === 'warning_only';
					const actions = getZoneCoverageActions(item);
					const assignedTechName =
						item.assignedTechName ||
						item.techName ||
						(assignedTechIdField && order?.[assignedTechIdField.name]);
					const assignedTechLabel = assignedTechName
						? `${assignedTechName} (#${item.assignedTechId})`
						: `técnico #${item.assignedTechId}`;
					return (
						<li
							key={`${item.zoneId || 'zone'}-${item.projectId || 'project'}-${
								item.serviceId || 'service'
							}-${item.assignedTechId || 'tech'}-${item.code || index}`}
							className={
								warningOnly ? styles.coverageWarning : styles.coverageReady
							}
						>
							<strong>
								{formatLocation({
									city: item.cityName,
									township: item.townshipName,
									zone: item.zoneName,
								})}
							</strong>
							{warningOnly ? (
								<>
									<p>{getIssueMessage(item)}</p>
									<span>
										Esta advertencia no cambia la asignación actual ni la
										cobertura del técnico. Resuelve las incidencias bloqueantes,
										si las hay, antes de importar.
									</span>
								</>
							) : (
								<>
									<p>
										Al confirmar la importación se aplicará este plan para{' '}
										<strong>{assignedTechLabel}</strong> en{' '}
										<strong>{getCoverageScopeLabel(item)}</strong>:
									</p>
									<ul className={styles.coverageActions}>
										{actions.map(action => (
											<li key={action}>
												{getCoverageActionLabel(action, item)}
											</li>
										))}
									</ul>
									{item.projectEligible === false && (
										<span className={styles.coverageCatalogNote}>
											La zona todavía no estaba habilitada para este alcance. El
											plan la incorporará a GPON más el servicio indicado.
										</span>
									)}
									{item.restoredDepartmentWideCoverage === true && (
										<span className={styles.coverageScopeWarning}>
											Al reactivar esta AUZ también se restaurará su cobertura
											histórica para todo el departamento, incluidas las
											relaciones AZD wildcard activas.
										</span>
									)}
									<span className={styles.coverageProvisionalNote}>
										Este es un mecanismo provisional del flujo de importación.
										La cobertura permanecerá hasta que un administrador la
										modifique.
									</span>
								</>
							)}
						</li>
					);
				})}
			</ul>
		</section>
	);
};

const CandidateButton = ({ candidate, label, onClick, disabled = false }) => (
	<button
		type='button'
		className={styles.candidateButton}
		onClick={() => onClick(candidate)}
		disabled={disabled}
	>
		<span>
			<strong>{label}</strong>
			<span>{formatLocation(candidate)}</span>
		</span>
		{confidenceLabel(candidate.confidence) && (
			<span className={styles.candidateConfidence}>
				{confidenceLabel(candidate.confidence)}
			</span>
		)}
	</button>
);

const ContractFrequencyReview = ({
	order,
	reviewOrders,
	contractFrequencyIdField,
	FrequencySelector,
	onApplyFrequency,
	onRequestFrequencyCreation,
	onSelectFrequencyCreation,
	onUndoFrequencyCreation,
	onDiscardFrequencyCreationRequest,
	frequencyCreationRequest,
	frequencyCreationSelected = false,
	isSubmitting = false,
}) => {
	const [creating, setCreating] = React.useState(false);
	const [draft, setDraft] = React.useState(() =>
		getContractFrequencyCreationDraft(order),
	);
	const [showCreationErrors, setShowCreationErrors] = React.useState(false);
	const [editingArtifact, setEditingArtifact] = React.useState();
	const nameInputRef = React.useRef(null);
	React.useEffect(() => {
		if (creating) nameInputRef.current?.focus();
	}, [creating]);
	if (!contractFrequencyIdField || contractFrequencyIdField.show !== true)
		return null;
	const frequency = getContractFrequencyPresentation(order);
	const legacyValue =
		(contractFrequencyIdField.name
			? order?.[contractFrequencyIdField.name]
			: undefined) ?? order?.[contractFrequencyIdField.id];
	const sourceValue = frequency.hasMatch ? frequency.sourceValue : legacyValue;
	const hasMatch = Boolean(
		frequency.status ||
			sourceValue !== undefined ||
			frequency.existing ||
			frequency.assigned ||
			frequency.candidates.length,
	);
	if (!hasMatch) return null;

	const assigned = frequency.assigned || frequency.existing;
	const hasPendingManualSelection =
		frequency.strategy === 'manual' &&
		order?.importDiagnostics?.needsRevalidation === true;
	const creation = getContractFrequencyCreationPresentation(order);
	const creationPlan = getContractFrequencyCreationPlan(order);
	const creationTargets = getContractFrequencyCreationCohortTargets(
		reviewOrders,
		order,
	);
	const updateDraft = (key, value) =>
		setDraft(current => ({ ...current, [key]: value }));
	const openCreationEditor = artifact => {
		setDraft(getContractFrequencyCreationDraft(order, artifact));
		setEditingArtifact(artifact);
		setShowCreationErrors(false);
		setCreating(true);
	};
	const creationErrors = getContractFrequencyCreationDraftErrors({
		orders: reviewOrders,
		sourceOrder: order,
		draft,
	});
	const canStartCreation =
		frequency.status === 'unknown' &&
		creation.canRequest &&
		!creationPlan &&
		!frequencyCreationRequest;
	const isRepreparing =
		creation.status === 'invalid' || Boolean(editingArtifact);
	const intervalUnitLabels = {
		day: 'día(s)',
		week: 'semana(s)',
		month: 'mes(es)',
		year: 'año(s)',
	};
	const activityMessage = hasPendingManualSelection
		? 'La frecuencia fue seleccionada manualmente y debe validarse antes de importar.'
		: frequency.activity?.code === 'CONTRACT_FREQUENCY_PRESERVED'
			? 'La celda está vacía; se conservará la frecuencia actual del contrato.'
			: frequency.activity?.code === 'CONTRACT_FREQUENCY_MANUALLY_SELECTED'
				? 'La frecuencia fue seleccionada manualmente y está validada para esta importación.'
				: undefined;

	return (
		<section
			className={styles.drawerSection}
			aria-labelledby='contract-frequency-heading'
		>
			<h3 id='contract-frequency-heading'>Frecuencia contractual</h3>
			<div className={styles.comparisonCards}>
				<div className={styles.comparisonCard}>
					<span className={styles.locationLabel}>Valor en archivo</span>
					<strong>{String(sourceValue ?? '').trim() || 'No informado'}</strong>
				</div>
				{frequency.existing && (
					<div className={styles.comparisonCard}>
						<span className={styles.locationLabel}>
							Frecuencia actual del contrato
						</span>
						<strong>{frequency.existing.name}</strong>
					</div>
				)}
				{(!frequency.existing ||
					frequency.assigned?.frequencyId !==
						frequency.existing.frequencyId) && (
					<div className={styles.comparisonCard}>
						<span className={styles.locationLabel}>
							Asignada en esta importación
						</span>
						<strong>{assigned?.name || 'Sin frecuencia asignada'}</strong>
					</div>
				)}
			</div>
			<p role='status'>{getContractFrequencyReviewMessage(order)}</p>
			{activityMessage && <p>{activityMessage}</p>}

			{frequency.canSelect && FrequencySelector ? (
				<div role='group' aria-label='Seleccionar frecuencia contractual'>
					<FrequencySelector
						field={{
							...contractFrequencyIdField,
							onChange: ({ option, rowData }) =>
								onApplyFrequency?.({ option, rowData }),
						}}
						rowData={order}
						disabled={isSubmitting}
					/>
				</div>
			) : frequency.mutable ? (
				<p className={styles.fieldError} role='alert'>
					No hay una opción firmada disponible. Valida nuevamente la
					importación.
				</p>
			) : null}

			{canStartCreation && !creating && (
				<button
					type='button'
					className={styles.zoneCreateButton}
					disabled={isSubmitting}
					onClick={() => openCreationEditor()}
				>
					<span aria-hidden='true'>+</span>{' '}
					{creation.status === 'invalid'
						? 'Repreparar frecuencia'
						: 'Crear frecuencia'}{' '}
					“{creation.sourceValue}”
				</button>
			)}

			{creation.status === 'invalid' && creation.reason && !creating && (
				<div className={styles.fieldError} role='alert'>
					{contractFrequencyCreationReasonLabel(creation.reason)}
				</div>
			)}

			{creating && (
				<fieldset
					className={styles.zoneCreationConfirm}
					disabled={isSubmitting}
				>
					<legend>Crear frecuencia contractual</legend>
					<label>
						<span>Nombre</span>
						<input
							ref={nameInputRef}
							name='frequencyName'
							required
							maxLength='80'
							aria-invalid={showCreationErrors && Boolean(creationErrors.name)}
							value={draft.name}
							onChange={event => updateDraft('name', event.target.value)}
						/>
					</label>
					<label>
						<span>Código</span>
						<input
							name='frequencyCode'
							required
							maxLength='32'
							pattern='[A-Z][A-Z0-9_]*'
							aria-invalid={showCreationErrors && Boolean(creationErrors.code)}
							value={draft.code}
							onChange={event => updateDraft('code', event.target.value)}
						/>
					</label>
					<div className={styles.frequencyCadence}>
						<label>
							<span>Repetir cada</span>
							<input
								name='frequencyIntervalCount'
								type='number'
								required
								min='1'
								max='2147483647'
								step='1'
								aria-invalid={
									showCreationErrors && Boolean(creationErrors.intervalCount)
								}
								value={draft.intervalCount}
								onChange={event =>
									updateDraft('intervalCount', event.target.value)
								}
							/>
						</label>
						<label>
							<span>Unidad</span>
							<select
								name='frequencyIntervalUnit'
								required
								aria-invalid={
									showCreationErrors && Boolean(creationErrors.intervalUnit)
								}
								value={draft.intervalUnit}
								onChange={event =>
									updateDraft('intervalUnit', event.target.value)
								}
							>
								<option value=''>Selecciona una unidad</option>
								{CONTRACT_FREQUENCY_INTERVAL_UNITS.map(unit => (
									<option key={unit} value={unit}>
										{intervalUnitLabels[unit]}
									</option>
								))}
							</select>
						</label>
					</div>
					<p>
						<strong>Alias del archivo: {draft.sourceAlias}</strong>
					</p>
					<p>
						El plan incluirá {creationTargets.length}{' '}
						{creationTargets.length === 1
							? 'orden con este valor'
							: 'órdenes con este valor'}
						, incluso si alguna está oculta por filtros.
					</p>
					<p>
						La frecuencia no se creará todavía. Primero debes validar el plan.
					</p>
					{showCreationErrors && Object.keys(creationErrors).length > 0 && (
						<div className={styles.fieldError} role='alert'>
							<ul>
								{Object.values(creationErrors).map(message => (
									<li key={message}>{message}</li>
								))}
							</ul>
						</div>
					)}
					<div className={styles.zoneCreationConfirmActions}>
						<button
							type='button'
							className={styles.secondaryButton}
							onClick={() => {
								setShowCreationErrors(false);
								setEditingArtifact(undefined);
								setCreating(false);
							}}
						>
							Volver
						</button>
						<button
							type='button'
							className={styles.primaryButton}
							onClick={() => {
								setShowCreationErrors(true);
								if (Object.keys(creationErrors).length > 0) return;
								onRequestFrequencyCreation?.({
									rowData: order,
									draft,
									...(editingArtifact ? { baseArtifact: editingArtifact } : {}),
								});
								setEditingArtifact(undefined);
								setCreating(false);
							}}
						>
							{isRepreparing ? 'Repreparar plan' : 'Preparar plan'}
						</button>
					</div>
				</fieldset>
			)}

			{frequencyCreationRequest && !creating && (
				<div className={styles.zoneCreationPlan} role='status'>
					<span className={styles.zoneCreationBadge}>Plan por validar</span>
					<strong>{frequencyCreationRequest.descriptor?.name}</strong>
					<span>
						Pulsa “Validar cambios” para comprobar la creación y su alcance.
					</span>
					<div className={styles.zoneCreationConfirmActions}>
						<button
							type='button'
							className={styles.secondaryButton}
							disabled={isSubmitting}
							onClick={() => openCreationEditor(frequencyCreationRequest)}
						>
							Editar
						</button>
						<button
							type='button'
							className={styles.zoneCreationUndo}
							disabled={isSubmitting}
							onClick={() =>
								onDiscardFrequencyCreationRequest?.(frequencyCreationRequest)
							}
						>
							Descartar
						</button>
					</div>
				</div>
			)}

			{creationPlan && !frequencyCreationRequest && !creating && (
				<div className={styles.zoneCreationPlan} role='status'>
					<span className={styles.zoneCreationBadge}>
						{creationPlan.mode === 'reuse'
							? 'Frecuencia existente'
							: 'Nueva frecuencia'}
					</span>
					<strong>
						{creationPlan.descriptor.name} ({creationPlan.descriptor.code})
					</strong>
					<span>
						Cada {creationPlan.descriptor.intervalCount}{' '}
						{intervalUnitLabels[creationPlan.descriptor.intervalUnit]}
					</span>
					<span>Alias: {creationPlan.descriptor.aliases.join(', ')}</span>
					<span>
						{creationPlan.mode === 'reuse'
							? 'Se reutilizará la frecuencia que ya existe en el catálogo.'
							: 'Se creará al confirmar la importación.'}
					</span>
					<span>
						Impacto: {creationPlan.targetRowIds.length}{' '}
						{creationPlan.targetRowIds.length === 1 ? 'orden' : 'órdenes'}.
					</span>
					<small>Filas incluidas: {creationPlan.targetRowIds.join(', ')}</small>
					<div className={styles.zoneCreationConfirmActions}>
						<button
							type='button'
							className={styles.secondaryButton}
							disabled={isSubmitting}
							onClick={() => openCreationEditor(creationPlan)}
						>
							Editar
						</button>
						<button
							type='button'
							className={
								frequencyCreationSelected
									? styles.zoneCreationUndo
									: styles.primaryButton
							}
							disabled={isSubmitting}
							onClick={() =>
								frequencyCreationSelected
									? onUndoFrequencyCreation?.(creationPlan)
									: onSelectFrequencyCreation?.(creationPlan)
							}
						>
							{frequencyCreationSelected ? 'Deshacer' : 'Agregar al plan'}
						</button>
					</div>
				</div>
			)}
		</section>
	);
};

const ServiceReview = ({
	order,
	reviewOrders,
	serviceIdField,
	issues = [],
	ManualServiceSelector,
	onApplyService,
	onStageLegacyService,
	onUndoServiceSelection,
	onRequestServiceCreation,
	onSelectServiceCreation,
	onUndoServiceCreation,
	serviceCreationSelected = false,
	isSubmitting = false,
}) => {
	const service = getServicePresentation(order);
	const diagnostics = getImportDiagnostics(order);
	const creation = getServiceCreationOption(order);
	const creationDiagnostics = diagnostics.serviceCreation || {};
	const catalog = getServiceCatalog(order);
	const serviceCandidates = getServiceSimilarityDisplayList(service.candidates);
	const serviceCatalog = getServiceCatalogDisplayList(catalog);
	const serviceIssue = serviceIdField
		? issues.find(issue => String(issue?.field) === String(serviceIdField.id))
		: undefined;
	const needsServiceRemediation = Boolean(
		serviceIssue && (!service.assigned || serviceIssue.blocking),
	);
	// An exact match can still be changed when the API explicitly permits it.
	// Keep that path limited to the current row's signed candidates, not an
	// unsigned display catalog or a client-side template permission.
	const selectableServiceCatalog =
		service.needsReview || needsServiceRemediation
			? serviceCatalog
			: service.allowManualSelection
				? serviceCandidates.filter(candidate => candidate.selectionChallenge)
				: [];
	const hasDuplicateServiceNames = [
		...serviceCandidates,
		...serviceCatalog,
	].some(option => option.hasNameCollision);
	const archetypeCatalog = getServiceArchetypeCatalog(order);
	const [creating, setCreating] = React.useState(false);
	const [name, setName] = React.useState('');
	const [archetypeServiceId, setArchetypeServiceId] = React.useState('');
	const canCreate =
		diagnostics.serviceCreation?.allowed === true ||
		diagnostics.serviceCreation?.canCreate === true ||
		diagnostics.serviceMatch?.canCreate === true;
	const hasServiceMatch =
		service.source !== undefined ||
		service.assigned ||
		service.candidates.length ||
		service.status ||
		service.strategy ||
		service.needsReview ||
		catalog.length ||
		canCreate ||
		needsServiceRemediation;
	if (service.isExisting)
		return (
			<section
				className={styles.drawerSection}
				aria-labelledby='service-heading'
			>
				<h3 id='service-heading'>Servicio</h3>
				<div className={styles.comparisonCards}>
					<div className={styles.comparisonCard}>
						<span className={styles.locationLabel}>Valor en archivo</span>
						<strong>
							{String(service.source || '').trim() || 'No informado'}
						</strong>
					</div>
					<div className={styles.comparisonCard}>
						<span className={styles.locationLabel}>
							Servicio actual en Synapse
						</span>
						<strong>
							{service.assigned?.name ||
								'No se pudo consultar el servicio actual'}
						</strong>
					</div>
				</div>
				<p>
					Esta orden ya existe. Su servicio se conservará y no puede modificarse
					desde esta importación.
				</p>
			</section>
		);
	if (!hasServiceMatch && !creation) return null;

	const assignedId = service.assigned?.id;
	const alternatives = serviceCandidates.filter(
		candidate => String(candidate.id) !== String(assignedId),
	);
	const affectedCount =
		creation?.affectedCount || creation?.targetRowIds?.length || 0;
	const targetRowIds = Array.isArray(creation?.targetRowIds)
		? creation.targetRowIds.map(String)
		: [];
	const archetype =
		creation?.plan?.archetype || creation?.plan?.archetypeService;
	const serviceCohortTargets = getServiceCohortTargets(reviewOrders, order);
	const canApplyToSimilar =
		service.strategy === 'manual' &&
		service.assigned &&
		serviceCohortTargets.length > 1;
	const hasPreviousServiceSelection =
		service.previous && Object.keys(service.previous).length > 0;
	const canStartServiceCreation = canCreate && !creating && !creation;
	const showManualServiceSelector = Boolean(
		needsServiceRemediation &&
			!serviceCandidates.length &&
			!serviceCatalog.length &&
			!service.assigned &&
			!creation &&
			!canCreate,
	);
	const pendingLegacyService =
		showManualServiceSelector &&
		typeof order?.[serviceIdField.id] === 'string' &&
		String(order[serviceIdField.id]).trim();
	const manualServiceRowData =
		order?.eventProps || !order?.exists?.eventProps
			? order
			: { ...order, eventProps: order.exists.eventProps };

	return (
		<section className={styles.drawerSection} aria-labelledby='service-heading'>
			<h3 id='service-heading'>Servicio</h3>
			<div className={styles.comparisonCards}>
				<div className={styles.comparisonCard}>
					<span className={styles.locationLabel}>Valor en archivo</span>
					<strong>
						{String(service.source || '').trim() || 'No informado'}
					</strong>
					{service.activityType && (
						<span>Actividad: {service.activityType}</span>
					)}
				</div>
				<div className={styles.comparisonCard}>
					<span className={styles.locationLabel}>Asignado en Synapse</span>
					<strong>{service.assigned?.name || 'Sin servicio asignado'}</strong>
					{service.assigned && confidenceLabel(service.confidence) && (
						<span className={styles.candidateConfidence}>
							{confidenceLabel(service.confidence)}
						</span>
					)}
				</div>
			</div>

			{hasDuplicateServiceNames && (
				<p className={styles.serviceDuplicateWarning} role='alert'>
					Existen servicios distintos con el mismo nombre. Verifica el ID antes
					de continuar.
				</p>
			)}

			{service.reasons.length > 0 && (
				<div className={styles.matchExplanation}>
					<h4>Cómo se obtuvo la coincidencia</h4>
					<ul className={styles.reasonList}>
						{service.reasons.map((reason, index) => (
							<li key={`service-reason-${index}`}>
								{getReasonMessage(reason)}
							</li>
						))}
					</ul>
				</div>
			)}

			{alternatives.length > 0 && (
				<div className={styles.matchExplanation}>
					<h4>Otras alternativas</h4>
					<div className={styles.candidateList}>
						{alternatives.map(candidate => (
							<button
								type='button'
								key={candidate.id}
								className={styles.candidateButton}
								disabled={isSubmitting}
								onClick={() =>
									onApplyService?.({ option: candidate, rowData: order })
								}
							>
								<span>
									<strong>{candidate.displayName}</strong>
								</span>
								{confidenceLabel(candidate.confidence) && (
									<span className={styles.candidateConfidence}>
										{confidenceLabel(candidate.confidence)}
									</span>
								)}
							</button>
						))}
					</div>
				</div>
			)}

			{selectableServiceCatalog.length > 0 && (
				<label className={styles.serviceSelectLabel}>
					<span>Seleccionar servicio manualmente</span>
					<select
						value=''
						disabled={isSubmitting}
						onChange={event => {
							const option = selectableServiceCatalog.find(
								item => String(item.id) === event.target.value,
							);
							if (option) onApplyService?.({ option, rowData: order });
						}}
					>
						<option value=''>Elige un servicio</option>
						{selectableServiceCatalog.map(option => (
							<option key={option.id} value={option.id}>
								{option.displayName}
							</option>
						))}
					</select>
				</label>
			)}
			{showManualServiceSelector && (
				<div
					className={styles.manualServiceSelector}
					role='group'
					aria-labelledby='manual-service-heading'
				>
					<strong id='manual-service-heading'>
						Seleccionar servicio manualmente
					</strong>
					<p>
						La selección quedará en esta importación hasta validar los cambios.
					</p>
					{pendingLegacyService && (
						<p className={styles.pendingServiceFeedback} role='status'>
							Servicio pendiente de validar: {pendingLegacyService}
						</p>
					)}
					{ManualServiceSelector ? (
						<ManualServiceSelector
							field={{
								...serviceIdField,
								onChange: ({ option, rowData, field }) =>
									onStageLegacyService?.({ option, rowData, field }),
							}}
							rowData={manualServiceRowData}
							selected={{ items: [] }}
						/>
					) : (
						<p className={styles.fieldError} role='alert'>
							No fue posible cargar el selector de servicios. Valida nuevamente
							la importación.
						</p>
					)}
					<small>
						Si el catálogo no carga o el selector no está disponible, reintenta
						o solicita los permisos de edición de servicio.
					</small>
				</div>
			)}
			{(hasPreviousServiceSelection || canStartServiceCreation) && (
				<div className={styles.serviceReviewActions}>
					{hasPreviousServiceSelection && (
						<button
							type='button'
							className={styles.zoneCreationUndo}
							disabled={isSubmitting}
							onClick={() => onUndoServiceSelection?.(order)}
						>
							Deshacer selección manual
						</button>
					)}
					{canStartServiceCreation && (
						<button
							type='button'
							className={styles.zoneCreateButton}
							disabled={isSubmitting}
							onClick={() => setCreating(true)}
						>
							<span aria-hidden='true'>+</span> Crear servicio desde arquetipo
						</button>
					)}
				</div>
			)}
			{creationDiagnostics.status === 'invalid' &&
				creationDiagnostics.reason && (
					<p className={styles.serviceCreationError} role='alert'>
						{serviceCreationReasonLabel(creationDiagnostics.reason)}
					</p>
				)}

			{creating && !creation && (
				<div
					className={styles.zoneCreationConfirm}
					role='group'
					aria-label='Crear servicio'
				>
					<label>
						<span>Nombre del nuevo servicio</span>
						<input
							name='newServiceName'
							value={name}
							onChange={event => setName(event.target.value)}
							disabled={isSubmitting}
						/>
					</label>
					<label>
						<span>Servicio base o arquetipo</span>
						<select
							name='serviceArchetype'
							value={archetypeServiceId}
							onChange={event => setArchetypeServiceId(event.target.value)}
							disabled={isSubmitting}
						>
							<option value=''>Selecciona un arquetipo</option>
							{archetypeCatalog.map(option => (
								<option key={option.id} value={option.id}>
									{option.name}
								</option>
							))}
						</select>
					</label>
					<p>
						El servicio no se creará todavía. Synapse validará el plan y su
						alcance antes de confirmar.
					</p>
					<div className={styles.zoneCreationConfirmActions}>
						<button
							type='button'
							className={styles.secondaryButton}
							onClick={() => setCreating(false)}
							disabled={isSubmitting}
						>
							Volver
						</button>
						<button
							type='button'
							className={styles.primaryButton}
							disabled={isSubmitting || !name.trim() || !archetypeServiceId}
							onClick={() =>
								onRequestServiceCreation?.({
									rowData: order,
									name: name.trim(),
									archetypeServiceId,
								})
							}
						>
							Validar plan
						</button>
					</div>
				</div>
			)}

			{creation && (
				<div className={styles.zoneCreationPlan} role='status'>
					<span className={styles.zoneCreationBadge}>Nuevo servicio</span>
					<strong>{creation.plan.name}</strong>
					{archetype && (
						<span>
							Clonará la configuración de{' '}
							{archetype.name || `servicio #${archetype.id}`}.
						</span>
					)}
					<span>{`Impacto: ${affectedCount} ${
						affectedCount === 1 ? 'orden seleccionada' : 'órdenes seleccionadas'
					} explícitamente.`}</span>
					{targetRowIds.length > 0 && (
						<small>Filas incluidas: {targetRowIds.join(', ')}</small>
					)}
					<button
						type='button'
						className={
							serviceCreationSelected
								? styles.zoneCreationUndo
								: styles.primaryButton
						}
						disabled={isSubmitting}
						onClick={() =>
							serviceCreationSelected
								? onUndoServiceCreation?.(creation)
								: onSelectServiceCreation?.(creation)
						}
					>
						{serviceCreationSelected ? 'Deshacer' : 'Agregar al plan'}
					</button>
				</div>
			)}

			{hasPreviousServiceSelection && canApplyToSimilar && (
				<div className={styles.serviceCohortAction}>
					<button
						type='button'
						className={styles.secondaryButton}
						disabled={isSubmitting}
						onClick={() =>
							onApplyService?.({
								option: service.assigned,
								rowData: order,
								applyToSimilar: true,
							})
						}
					>
						Aplicar a valores iguales
					</button>
					<small>
						Se aplicará a {serviceCohortTargets.length}{' '}
						{serviceCohortTargets.length === 1 ? 'orden' : 'órdenes'} de esta
						importación, incluidas las que no estén visibles por filtros.
					</small>
				</div>
			)}
		</section>
	);
};

const DateDefaultsReview = ({ order }) => {
	const diagnostics = getImportDiagnostics(order);
	const defaults = Array.isArray(diagnostics.dateDefaults)
		? diagnostics.dateDefaults
		: Array.isArray(diagnostics.date_defaults)
			? diagnostics.date_defaults
			: [];
	if (!defaults.length) return null;
	return (
		<section
			className={styles.drawerSection}
			aria-labelledby='date-defaults-heading'
		>
			<h3 id='date-defaults-heading'>Fechas autocorregidas</h3>
			<ul className={styles.dateDefaultList}>
				{defaults.map((item, index) => (
					<li key={item.field || index}>
						<strong>{item.label || item.field || 'Fecha operativa'}</strong>
						<span>
							Archivo:{' '}
							{String(item.source ?? item.sourceValue ?? '').trim() ||
								'No informado'}
						</span>
						<span>
							Synapse: {item.assigned || item.value || order?.[item.field]}
						</span>
						<small>Se usó la fecha del momento de procesamiento.</small>
					</li>
				))}
			</ul>
		</section>
	);
};

const AssignmentParty = ({ label, party, emphasis = false }) => (
	<div
		className={`${styles.assignmentCard} ${
			emphasis ? styles.assignmentCardEmphasis : ''
		}`}
	>
		<span className={styles.locationLabel}>{label}</span>
		<strong>{party?.name || party?.workCode || 'Sin técnico asignado'}</strong>
		{party?.workCode && <span>Identificador: {party.workCode}</span>}
		{party?.entityName && <span>Entidad: {party.entityName}</span>}
		{(party?.supervisorName || party?.supervisorId) && (
			<span>
				Supervisor: {party.supervisorName || `#${party.supervisorId}`}
				{party.supervisorWorkCode ? ` · ${party.supervisorWorkCode}` : ''}
			</span>
		)}
	</div>
);

const AssignmentReview = ({
	order,
	assignedTechIdField,
	AssignmentSelector,
	assignmentReviewSelection,
	onStageAssignmentSelection,
	isSubmitting,
}) => {
	const assignment = getAssignmentPresentation(order, assignedTechIdField);
	const diagnostics = getImportDiagnostics(order);
	const hasAssignmentDiagnostics =
		diagnostics.assignment && Object.keys(diagnostics.assignment).length > 0;
	if (!assignedTechIdField && !hasAssignmentDiagnostics) return null;
	const pendingCandidate = assignmentReviewSelection
		? assignment.candidates.find(
				candidate =>
					candidate.selectionChallenge === assignmentReviewSelection.challenge,
			)
		: undefined;
	const canSelect =
		assignedTechIdField &&
		assignment.mutability.allowed === true &&
		assignment.candidates.length > 0 &&
		AssignmentSelector &&
		onStageAssignmentSelection;
	const selectorRowData =
		pendingCandidate && assignedTechIdField
			? {
					...order,
					[assignedTechIdField.id]: pendingCandidate.assignedTechId,
					[assignedTechIdField.name]:
						pendingCandidate.name || pendingCandidate.workCode,
				}
			: order;

	return (
		<section
			className={styles.drawerSection}
			aria-labelledby='assignment-heading'
		>
			<h3 id='assignment-heading'>Asignación del técnico</h3>
			{assignment.previousResolved && (
				<div className={styles.assignmentDriftNotice} role='status'>
					<strong>La asignación cambió durante la validación final.</strong>
					<span>Revisa el valor anterior y el nuevo antes de confirmar.</span>
				</div>
			)}
			<div className={styles.assignmentCards}>
				<AssignmentParty label='Valor en archivo' party={assignment.source} />
				{assignment.existing && (
					<AssignmentParty
						label='Asignación actual'
						party={assignment.existing}
					/>
				)}
				{assignment.previousResolved && (
					<AssignmentParty
						label='Asignación revisada anteriormente'
						party={assignment.previousResolved}
					/>
				)}
				<AssignmentParty
					label='Asignado por Synapse'
					party={assignment.resolved}
					emphasis
				/>
				{pendingCandidate && (
					<AssignmentParty
						label='Selección pendiente'
						party={pendingCandidate}
					/>
				)}
			</div>
			{canSelect ? (
				<div
					className={styles.assignmentSelector}
					role='group'
					aria-labelledby={`assignment-selector-${getImportRowIdentity(order)}-label`}
				>
					<strong
						id={`assignment-selector-${getImportRowIdentity(order)}-label`}
					>
						Cambiar asignación
					</strong>
					<div>
						<AssignmentSelector
							field={{
								...assignedTechIdField,
								onChange: onStageAssignmentSelection,
							}}
							rowData={selectorRowData}
							selected={{ items: [] }}
							options={assignment.candidates}
							authorized
							disabled={isSubmitting}
							selectedOptionChallenge={assignmentReviewSelection?.challenge}
						/>
					</div>
					{pendingCandidate && (
						<p className={styles.assignmentPendingNotice} role='status'>
							Selección pendiente. Pulsa “Validar cambios” para confirmarla.
						</p>
					)}
				</div>
			) : (
				<p className={styles.assignmentImmutableNotice} role='status'>
					{assignment.mutability.allowed === true
						? `${assignment.existing?.assignedTechId ? 'Synapse conservará la asignación actual. ' : ''}Vuelve a validar la importación para cambiar el técnico. Las opciones autorizadas no están disponibles en esta revisión.`
						: getAssignmentMutabilityMessage(assignment.mutability)}
				</p>
			)}
			{assignment.explanation && (
				<p className={styles.assignmentExplanation}>{assignment.explanation}</p>
			)}
		</section>
	);
};

const LocationReview = ({
	order,
	locationFields,
	zoneIdField,
	onApplyCandidate,
	onSelectZoneCreation,
	onUndoZoneCreation,
	zoneCreationSelection,
	ManualZoneSelector,
	isSubmitting = false,
}) => {
	const [isConfirmingCreation, setIsConfirmingCreation] = React.useState(false);
	const confirmationHeadingRef = React.useRef(null);
	const fieldId = zoneIdField?.id;
	const source = getSourceValues(order, fieldId);
	const sourceProvenance = getSourceProvenance(order, fieldId);
	const presentation = getLocationPresentation({
		order,
		...locationFields,
		zoneIdField,
	});
	const match = getLocationMatch(order, fieldId);
	const presentedOption = getCandidateOption(presentation.option);
	const candidates = getOrderCandidates(order, fieldId).filter(
		candidate => candidate.zoneId !== presentedOption.zoneId,
	);
	const hasSourceLocation = hasLocationSourceValue(source);
	const isMissingZoneFallback = match.strategy === 'parent_fallback';
	const isSuggestion =
		presentation.state === LOCATION_PRESENTATION_STATE.SUGGESTED;
	const sectionId = `location-${fieldId || 'legacy'}`;
	const zoneCreationOption = getZoneCreationOption(order, fieldId);
	const existingOrder = isExistingImportOrder(order);
	const zoneLevelLabels = {
		city: locationFields?.cityIdField?.name,
		township: locationFields?.townshipIdField?.name,
		zone: zoneIdField?.name,
	};
	const activeZoneCreation =
		!existingOrder &&
		(zoneCreationSelection ||
			(zoneCreationOption?.status === 'selected'
				? zoneCreationOption
				: undefined));
	const canCreateZone =
		zoneCreationOption?.status === 'available' &&
		!activeZoneCreation &&
		['city', 'township', 'zone'].every(key =>
			String(zoneCreationOption?.source?.[key] || '').trim(),
		);
	const creationSource =
		activeZoneCreation?.source || zoneCreationOption?.source;
	const creationLevels = newZoneCreationLevels(
		activeZoneCreation?.levels || zoneCreationOption?.levels,
	);
	const createsHierarchy = creationLevels.length > 1;
	const creationAffectedCount = Math.max(
		0,
		Number(
			activeZoneCreation?.affectedCount ||
				zoneCreationOption?.affectedCount ||
				0,
		),
	);
	const previousCreationAssignment = activeZoneCreation?.previous?.assigned;
	const previousCreationLocation = previousCreationAssignment
		? formatLocation({
				city:
					previousCreationAssignment.cityName ||
					previousCreationAssignment.city,
				township:
					previousCreationAssignment.townshipName ||
					previousCreationAssignment.township,
				zone:
					previousCreationAssignment.zoneName ||
					previousCreationAssignment.zone,
			})
		: formatLocation(presentation.location);
	const displayedPresentation =
		activeZoneCreation &&
		presentation.state !== LOCATION_PRESENTATION_STATE.PENDING_CREATION
			? {
					...presentation,
					state: LOCATION_PRESENTATION_STATE.PENDING_CREATION,
					label: 'Nueva zona al confirmar',
					location: creationSource,
				}
			: presentation;
	const showManualZoneSelector =
		!isSubmitting &&
		typeof onApplyCandidate === 'function' &&
		zoneIdField &&
		locationFields?.cityIdField &&
		locationFields?.townshipIdField &&
		ManualZoneSelector &&
		!isCanonicalLocationOption(getCandidateOption(match.assigned));

	React.useEffect(() => {
		setIsConfirmingCreation(false);
	}, [order, fieldId, zoneCreationOption?.challenge]);
	React.useEffect(() => {
		if (isConfirmingCreation && confirmationHeadingRef.current)
			confirmationHeadingRef.current.focus();
	}, [isConfirmingCreation]);

	return (
		<section className={styles.drawerSection} aria-labelledby={sectionId}>
			<h3 id={sectionId}>{getZoneLabel(zoneIdField)}</h3>
			<div className={styles.comparisonCards}>
				<div className={styles.comparisonCard}>
					<span className={styles.locationLabel}>Valor en archivo</span>
					<strong>{formatLocation(source)}</strong>
					{sourceProvenance.selectedColumn && (
						<span className={styles.sourceProvenance}>
							Se usó {sourceProvenance.selectedColumn}:{' '}
							<strong>{String(sourceProvenance.selectedValue).trim()}</strong>.{' '}
							{sourceProvenance.primaryColumn} venía como{' '}
							<strong>
								{String(sourceProvenance.primaryValue ?? '').trim() || 'vacío'}
							</strong>
							.
						</span>
					)}
					{canCreateZone && (
						<>
							<ZoneCreationLevels
								levels={zoneCreationOption?.levels}
								labels={zoneLevelLabels}
							/>
							<button
								type='button'
								className={styles.zoneCreateButton}
								onClick={() => setIsConfirmingCreation(true)}
								disabled={isSubmitting}
								aria-describedby={`${sectionId}-create-help`}
							>
								<span aria-hidden='true'>+</span> Crear y seleccionar esta zona
							</button>
							<span
								id={`${sectionId}-create-help`}
								className={styles.zoneCreateHelp}
							>
								Verifica la ortografía. Se creará solo al confirmar y se
								aplicará a {creationAffectedCount}{' '}
								{creationAffectedCount === 1 ? 'orden' : 'órdenes'}.
							</span>
						</>
					)}
				</div>
				<div className={styles.comparisonCard}>
					<span className={styles.locationLabel}>
						{displayedPresentation.label}
					</span>
					<strong>
						{displayedPresentation.state ===
						LOCATION_PRESENTATION_STATE.UNASSIGNED
							? 'Todavía no se seleccionó una ubicación.'
							: formatLocation(displayedPresentation.location)}
					</strong>
					{displayedPresentation.state ===
						LOCATION_PRESENTATION_STATE.ASSIGNED &&
						confidenceLabel(getDisplayConfidence(match)) && (
							<span className={styles.candidateConfidence}>
								{confidenceLabel(getDisplayConfidence(match))}
							</span>
						)}
					{displayedPresentation.zoneCreationResult && (
						<ZoneCreationLevels
							levels={displayedPresentation.zoneCreationResult.levels}
							labels={zoneLevelLabels}
							result
						/>
					)}
				</div>
			</div>

			{isConfirmingCreation && canCreateZone && (
				<div
					className={styles.zoneCreationConfirm}
					role='group'
					aria-labelledby={`${sectionId}-create-title`}
				>
					<strong
						id={`${sectionId}-create-title`}
						ref={confirmationHeadingRef}
						tabIndex='-1'
					>
						{createsHierarchy
							? 'Confirmar nueva jerarquía'
							: 'Confirmar nueva zona'}
					</strong>
					<ZoneCreationLevels
						levels={activeZoneCreation?.levels || zoneCreationOption?.levels}
						labels={zoneLevelLabels}
					/>
					<span>
						Nueva: <strong>{formatLocation(creationSource)}</strong>
					</span>
					<span>Selección anterior: {previousCreationLocation}</span>
					<p>
						{createsHierarchy &&
							`Este cambio manual y controlado agregará ${creationLevels.length} niveles al catálogo de ubicaciones. `}
						Se aplicará a {creationAffectedCount}{' '}
						{creationAffectedCount === 1
							? 'orden de esta importación'
							: 'órdenes de esta importación'}
						, incluidas las que no estén visibles por filtros.
					</p>
					<div className={styles.zoneCreationConfirmActions}>
						<button
							type='button'
							className={styles.secondaryButton}
							onClick={() => setIsConfirmingCreation(false)}
							disabled={isSubmitting}
						>
							Volver
						</button>
						<button
							type='button'
							className={styles.primaryButton}
							onClick={() => {
								onSelectZoneCreation(zoneCreationOption);
								setIsConfirmingCreation(false);
							}}
							disabled={isSubmitting}
						>
							Agregar al plan
						</button>
					</div>
				</div>
			)}

			{activeZoneCreation && (
				<div
					className={styles.zoneCreationPlan}
					role='status'
					aria-live='polite'
				>
					<span className={styles.zoneCreationBadge}>Pendiente de crear</span>
					<span>Anterior: {previousCreationLocation}</span>
					<ZoneCreationLevels
						levels={activeZoneCreation.levels}
						labels={zoneLevelLabels}
					/>
					<strong>
						Nueva zona al confirmar: {formatLocation(creationSource)}
					</strong>
					<span>
						Se aplicará a {creationAffectedCount}{' '}
						{creationAffectedCount === 1 ? 'orden' : 'órdenes'} de esta
						importación.
					</span>
					<button
						type='button'
						className={styles.zoneCreationUndo}
						onClick={() => onUndoZoneCreation(activeZoneCreation)}
						disabled={isSubmitting}
					>
						Deshacer
					</button>
				</div>
			)}

			{isSuggestion && presentedOption.zoneId && (
				<div className={styles.candidateActions}>
					<CandidateButton
						candidate={{
							...presentedOption,
							confidence: getDisplayConfidence(match),
						}}
						label='Aplicar sugerencia'
						onClick={candidate =>
							onApplyCandidate({
								option: candidate,
								rowData: order,
								fieldId,
							})
						}
						disabled={isSubmitting}
					/>
					{hasSourceLocation && (
						<button
							type='button'
							className={styles.secondaryButton}
							onClick={() =>
								onApplyCandidate({
									option: presentedOption,
									rowData: order,
									fieldId,
									applyToSimilar: true,
								})
							}
							disabled={isSubmitting}
						>
							Aplicar a valores iguales
						</button>
					)}
				</div>
			)}

			{Array.isArray(match.reasons) && match.reasons.length > 0 && (
				<div className={styles.matchExplanation}>
					<h4>Cómo se obtuvo la coincidencia</h4>
					<ul className={styles.reasonList}>
						{match.reasons.map((reason, index) => (
							<li key={`${fieldId || 'legacy'}-reason-${index}`}>
								{getReasonMessage(reason)}
							</li>
						))}
					</ul>
				</div>
			)}

			{candidates.length > 0 && (
				<div className={styles.matchExplanation}>
					<h4>Otras alternativas</h4>
					<div className={styles.candidateList}>
						{candidates.map((candidate, index) => (
							<CandidateButton
								key={`${fieldId || 'legacy'}-${candidate.zoneId || index}`}
								candidate={
									isMissingZoneFallback
										? { ...candidate, confidence: undefined }
										: candidate
								}
								label={`${
									isMissingZoneFallback
										? 'Cambiar a alternativa'
										: 'Alternativa'
								} ${index + 1}`}
								onClick={option =>
									onApplyCandidate({
										option,
										rowData: order,
										fieldId,
									})
								}
								disabled={isSubmitting}
							/>
						))}
					</div>
				</div>
			)}

			{showManualZoneSelector && (
				<div
					className={styles.manualZoneSelector}
					role='group'
					aria-labelledby={`${sectionId}-manual-heading`}
				>
					<strong id={`${sectionId}-manual-heading`}>
						Seleccionar ubicación manualmente
					</strong>
					<p>
						Elige una ruta completa. La selección se validará antes de importar
						y no modificará órdenes existentes.
					</p>
					<ManualZoneSelector
						field={{
							...zoneIdField,
							onChange: ({ option, rowData }) =>
								onApplyCandidate({ option, rowData, fieldId }),
						}}
						rowData={order}
						selected={{ items: [] }}
					/>
				</div>
			)}
		</section>
	);
};

const OrderIssuesDrawer = ({
	order,
	orders,
	reviewOrders = orders,
	manualZoneSelector,
	manualServiceSelector,
	frequencySelector,
	assignmentSelector,
	assignmentReviewSelection,
	onStageAssignmentSelection,
	fields,
	odtIdField,
	locationFields,
	serviceIdField,
	contractFrequencyIdField,
	assignedTechIdField,
	onClose,
	fieldReviewSelections = [],
	onApplyFieldReview,
	onUndoFieldReview,
	onDeleteOrder,
	onOpenOrder,
	onApplyCandidate,
	onSelectZoneCreation,
	onUndoZoneCreation,
	onApplyService,
	onApplyFrequency,
	onRequestFrequencyCreation,
	onSelectFrequencyCreation,
	onUndoFrequencyCreation,
	onDiscardFrequencyCreationRequest,
	frequencyCreationRequests = [],
	frequencyCreationSelections = [],
	onStageLegacyService,
	onUndoServiceSelection,
	onRequestServiceCreation,
	onSelectServiceCreation,
	onUndoServiceCreation,
	nextOrderLabel = 'Siguiente problema',
	serviceCreationSelections = [],
	zoneCreationSelections = [],
	themeStyle,
	themeProfile,
	isSubmitting = false,
}) => {
	if (!order) return null;

	const identifier = getOrderIdentifier(order, odtIdField);
	const effectiveReview = getEffectiveOrderReviewState({
		order,
		fields,
		locationFields,
		contractFrequencyCreationSelections: frequencyCreationSelections,
	});
	const { issues } = effectiveReview;
	const configuredZoneFields = Array.isArray(locationFields?.zoneFields)
		? locationFields.zoneFields
		: locationFields?.zoneIdField
			? [locationFields.zoneIdField]
			: [undefined];
	const zoneFields = getApplicableZoneFields(order, configuredZoneFields);
	const primaryMatch = getLocationMatch(order, zoneFields[0]?.id);
	const primaryPresentation = getLocationPresentation({
		order,
		...locationFields,
		zoneIdField: zoneFields[0],
	});
	const nextOrder = findNextReviewOrder({
		orders,
		currentOrder: order,
	});

	return (
		<Drawer
			className={styles.importReviewDrawer}
			style={themeStyle}
			headerBackgroundColor='var(--import-review-background)'
			headerColor='var(--import-review-text)'
			placement='right'
			visible={true}
			closable={false}
			keyboard={!isSubmitting}
			maskClosable={!isSubmitting}
			destroyOnClose
			$maxWidth='500px'
			$fullWidthBelow='720px'
			onClose={isSubmitting ? undefined : onClose}
			title={`Revisión · ${identifier}`}
		>
			<div
				className={styles.drawerContent}
				style={themeStyle}
				data-theme={themeProfile}
			>
				{onDeleteOrder && (
					<div className={styles.drawerDangerAction}>
						<button
							type='button'
							className={styles.dangerButton}
							disabled={isSubmitting}
							onClick={() => {
								const rowIdentity = getImportRowIdentity(order);
								if (rowIdentity !== undefined && rowIdentity !== null)
									onDeleteOrder([String(rowIdentity)]);
							}}
						>
							Eliminar orden de esta importación
						</button>
						<small>
							Solo se quitará del lote actual; no se eliminará de Synapse.
						</small>
					</div>
				)}
				<div className={styles.drawerIntro}>
					<MatchStatusBadge
						status={effectiveReview.status}
						confidence={
							primaryPresentation.state ===
							LOCATION_PRESENTATION_STATE.UNASSIGNED
								? undefined
								: getDisplayConfidence(primaryMatch)
						}
					/>
					{order?.importDiagnostics?.rowNumber && (
						<span>Fila {order.importDiagnostics.rowNumber} del archivo</span>
					)}
				</div>

				<FieldReviewEditors
					order={order}
					selections={fieldReviewSelections}
					onApply={onApplyFieldReview}
					onUndo={onUndoFieldReview}
					isSubmitting={isSubmitting}
				/>

				{zoneFields.map((zoneIdField, index) => (
					<LocationReview
						key={zoneIdField?.id || index}
						order={order}
						locationFields={locationFields}
						zoneIdField={zoneIdField}
						onApplyCandidate={onApplyCandidate}
						onSelectZoneCreation={onSelectZoneCreation}
						onUndoZoneCreation={onUndoZoneCreation}
						zoneCreationSelection={zoneCreationSelections.find(selection =>
							isOrderTargetedByZoneCreation(order, zoneIdField?.id, selection),
						)}
						ManualZoneSelector={manualZoneSelector}
						isSubmitting={isSubmitting}
					/>
				))}

				<ContractFrequencyReview
					key={getImportRowIdentity(order)}
					order={order}
					reviewOrders={reviewOrders}
					contractFrequencyIdField={contractFrequencyIdField}
					FrequencySelector={frequencySelector}
					onApplyFrequency={onApplyFrequency}
					onRequestFrequencyCreation={onRequestFrequencyCreation}
					onSelectFrequencyCreation={onSelectFrequencyCreation}
					onUndoFrequencyCreation={onUndoFrequencyCreation}
					onDiscardFrequencyCreationRequest={onDiscardFrequencyCreationRequest}
					frequencyCreationRequest={frequencyCreationRequests.find(
						request =>
							request.cohortKey ===
							getContractFrequencyCreationPresentation(order).cohortKey,
					)}
					frequencyCreationSelected={frequencyCreationSelections.some(
						selection =>
							selection.challenge ===
							getContractFrequencyCreationPlan(order)?.challenge,
					)}
					isSubmitting={isSubmitting}
				/>

				<ServiceReview
					order={order}
					reviewOrders={reviewOrders}
					serviceIdField={serviceIdField}
					issues={issues}
					ManualServiceSelector={manualServiceSelector}
					onApplyService={onApplyService}
					onStageLegacyService={onStageLegacyService}
					onUndoServiceSelection={onUndoServiceSelection}
					onRequestServiceCreation={onRequestServiceCreation}
					onSelectServiceCreation={onSelectServiceCreation}
					onUndoServiceCreation={onUndoServiceCreation}
					serviceCreationSelected={serviceCreationSelections.some(
						selection =>
							selection.challenge ===
							getServiceCreationOption(order)?.challenge,
					)}
					isSubmitting={isSubmitting}
				/>

				<DateDefaultsReview order={order} />

				<AssignmentReview
					order={order}
					assignedTechIdField={assignedTechIdField}
					AssignmentSelector={assignmentSelector}
					assignmentReviewSelection={assignmentReviewSelection}
					onStageAssignmentSelection={onStageAssignmentSelection}
					isSubmitting={isSubmitting}
				/>

				<CoverageReview
					order={order}
					assignedTechIdField={assignedTechIdField}
				/>

				<section
					className={styles.drawerSection}
					aria-labelledby='issues-heading'
				>
					<h3 id='issues-heading'>Incidencias</h3>
					{issues.length ? (
						<ul className={styles.issueList}>
							{issues.map((issue, index) => (
								<li
									key={`${issue?.field || 'order'}-${issue?.code || index}-${index}`}
								>
									<span
										aria-hidden='true'
										className={
											issue?.blocking
												? styles.issueIconBlocking
												: styles.issueIconReview
										}
									>
										{issue?.blocking ? '×' : '!'}
									</span>
									<div>
										<strong>{getIssueLabel(issue, order)}</strong>
										<p>{getIssueMessage(issue, order)}</p>
									</div>
								</li>
							))}
						</ul>
					) : (
						<p className={styles.muted}>Esta orden no tiene incidencias.</p>
					)}
				</section>

				<div className={styles.drawerFooter}>
					<button
						type='button'
						className={styles.secondaryButton}
						onClick={onClose}
						disabled={isSubmitting}
					>
						Cerrar
					</button>
					{nextOrder && nextOrder !== order && (
						<button
							type='button'
							className={styles.primaryButton}
							onClick={() => onOpenOrder(nextOrder)}
							disabled={isSubmitting}
						>
							{nextOrderLabel}
						</button>
					)}
				</div>
			</div>
		</Drawer>
	);
};

export default OrderIssuesDrawer;
